// ⚠️ MOCK Spin 服務 — 用本機隨機盤面 + Mock 賠付表產生結果，不連真實後端。
// 取得真實後端/協定後，應改用實作同一 SpinService 介面的 RemoteSpinService（見 docs/PIXI_MIGRATION_PLAN.md 階段 1）。

import {
  BUY_TYPE_FREE_SPINS,
  FG_MAX_SPINS,
  FG_MULTIPLIER_INCREMENT,
  FG_SPIN_COUNT,
  FG_START_MULTIPLIER,
  MAIN_REEL_COUNT,
  MOCK_SCATTER_LAND_CHANCE,
  MOCK_SCATTER_MAX_COLLECT,
  SCATTER_FG_TRIGGER_COUNT,
  SCATTER_SYMBOL_ID,
  TRACKER_OVERLAY_REELS,
  TRACKER_REEL_INDEX,
  VISIBLE_ROWS,
} from '../constants'
import { pickReelWindow } from '../config/reelStrips'
import { applyFreeGameMultiplier, resolveTumbles, type TumbleOutcome } from '../logic/tumbleEngine'
import { collectScatters } from '../logic/scatterCollector'
import { injectMockTallSymbols } from '../logic/tallSymbols'
import type { Board, ReelColumn } from '../model/board'
import type { FreeGameOutcome, FreeGameSpinResult, SpinRequest, SpinResponse, SpinService } from './SpinService'

function generateBoard(): Board {
  const board: Board = Array.from({ length: MAIN_REEL_COUNT }, (_, reelIndex) =>
    pickReelWindow(reelIndex, VISIBLE_ROWS).map((symbol) => ({ symbol })),
  )
  // [MOCK] 落盤時注入長條大符號（見 logic/tallSymbols.ts）。
  return injectMockTallSymbols(board)
}

function generateTrackerColumn(): ReelColumn {
  return pickReelWindow(TRACKER_REEL_INDEX, TRACKER_OVERLAY_REELS.length).map((symbol) => ({ symbol }))
}

function countScatters(board: Board): number {
  return board.reduce(
    (count, column) => count + column.filter((cell) => cell.symbol === SCATTER_SYMBOL_ID).length,
    0,
  )
}

/**
 * [MOCK] 本轉「落地」的 Scatter 數：實際盤面 Scatter（上限 2）；沒有時偶爾注入 1 顆，純為離線可見計量玩法。
 * 累積達 MOCK_SCATTER_MAX_COLLECT 後不再注入，避免 retrigger 無限延長 FG。
 */
function mockScatterLandCount(board: Board, currentCount: number): number {
  if (currentCount >= MOCK_SCATTER_MAX_COLLECT) return 0
  const actual = countScatters(board)
  if (actual > 0) return Math.min(actual, 2)
  return Math.random() < MOCK_SCATTER_LAND_CHANCE ? 1 : 0
}

function runFreeGame(betPerWay: number): FreeGameOutcome {
  const spins: FreeGameSpinResult[] = []
  let multiplier = FG_START_MULTIPLIER
  let totalWin = 0
  // Scatter 計量狀態（跨轉累積，見 logic/scatterCollector.ts）。
  let scatterCount = 0
  let awardIndex = 0
  // 總轉數動態成長：Scatter 獎勵的加免費次數（retrigger）會延長本 FG，上限 FG_MAX_SPINS。
  let target = FG_SPIN_COUNT

  for (let i = 0; i < target && i < FG_MAX_SPINS; i++) {
    const startMultiplier = multiplier
    const rawOutcome = resolveTumbles(generateBoard(), generateTrackerColumn(), betPerWay)
    const applied = applyFreeGameMultiplier(rawOutcome, startMultiplier, FG_MULTIPLIER_INCREMENT)

    // 本轉落地的 Scatter 逐顆收集：可能升級、發放獎勵（加倍率/加免費次數）。
    const collect = collectScatters(scatterCount, mockScatterLandCount(rawOutcome.finalBoard, scatterCount), awardIndex)
    scatterCount = collect.endCount
    awardIndex = collect.awardIndexEnd
    // 獎勵倍率併入 FG 累積倍率（影響後續轉）；retrigger 加免費次數（延長本 FG）。
    multiplier = applied.endingMultiplier + collect.addedMultiplier
    target = Math.min(target + collect.addedSpins, FG_MAX_SPINS)

    spins.push({
      steps: applied.steps,
      totalWin: applied.totalWin,
      finalBoard: rawOutcome.finalBoard,
      trackerColumn: rawOutcome.trackerColumn,
      startMultiplier,
      endMultiplier: multiplier,
      scatterEvents: collect.events,
    })
    totalWin += applied.totalWin
  }

  return { spins, spinCount: FG_SPIN_COUNT, totalWin }
}

export class MockSpinService implements SpinService {
  async spin(request: SpinRequest): Promise<SpinResponse> {
    // MOCK 簡化：每個 way 的下注額直接等於 totalBet（真實比例待賠付表/後端數值確認）。
    const betPerWay = request.totalBet

    // 購買免費遊戲（BuyType=2）：比照 Unity 跳過主遊戲、直接進入免費遊戲。
    // 主遊戲僅回一個無中獎盤面供畫面短暫顯示，隨即轉場免費遊戲。
    if (request.buyType === BUY_TYPE_FREE_SPINS) {
      const mainGame: TumbleOutcome = {
        steps: [],
        totalWin: 0,
        finalBoard: generateBoard(),
        trackerColumn: generateTrackerColumn(),
      }
      const freeGame = runFreeGame(betPerWay)
      return {
        mainGame,
        scatterCount: SCATTER_FG_TRIGGER_COUNT,
        triggeredFreeGame: true,
        freeGame,
        totalWin: freeGame.totalWin,
      }
    }

    const mainGame = resolveTumbles(generateBoard(), generateTrackerColumn(), betPerWay)
    const scatterCount = countScatters(mainGame.finalBoard)
    const triggeredFreeGame = scatterCount >= SCATTER_FG_TRIGGER_COUNT
    const freeGame = triggeredFreeGame ? runFreeGame(betPerWay) : undefined

    return {
      mainGame,
      scatterCount,
      triggeredFreeGame,
      ...(freeGame !== undefined ? { freeGame } : {}),
      totalWin: mainGame.totalWin + (freeGame?.totalWin ?? 0),
    }
  }
}
